import { DocumentReference, onSnapshot, getDoc } from 'firebase/firestore'
import { useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isDemoMode } from 'demo/config'

export interface UseFirestoreDocOptions<T = unknown> {
  /** Field name to use for the document ID (default: 'id') */
  idField?: string
  /** Whether to disable the query (default: false) */
  disabled?: boolean
  /** Resolver used instead of Firestore when demo mode is enabled. */
  demo?: () => Promise<T | undefined>
}

export interface UseFirestoreDocResult<T> {
  /** The document data with ID field added */
  data: T | undefined
  /** Whether the initial fetch is still loading */
  status: 'error' | 'success' | 'pending'
  /** Any error that occurred */
  error: Error | null
}

/**
 * Hook to subscribe to a Firestore document with real-time updates
 * Uses TanStack Query for caching and state management
 */
export function useFirestoreDoc<T>(
  docRef: DocumentReference<T>,
  options: UseFirestoreDocOptions<T & { id?: string }> = {},
): UseFirestoreDocResult<T & { id?: string }> {
  const { idField = 'id' } = options
  const queryClient = useQueryClient()

  // In demo mode the resolver replaces Firestore entirely. `isDemoMode` is a
  // build-time constant, so this never flips at runtime and hook order is stable.
  const demo = isDemoMode ? options.demo : undefined

  // Create a stable query key from the document path
  const queryKey = useMemo(() => ['firestore', 'doc', docRef.path], [docRef.path])

  // Set up real-time listener
  useEffect(() => {
    if (demo) return

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = {
            ...snapshot.data(),
            [idField]: snapshot.id,
          } as T & { id?: string }

          queryClient.setQueryData(queryKey, data)
        } else {
          queryClient.setQueryData(queryKey, undefined)
        }
      },
      (error) => {
        console.error('Firestore doc error:', error)
        queryClient.setQueryData(queryKey, undefined)
      },
    )

    return () => unsubscribe()
  }, [docRef, queryKey, queryClient, idField, demo])

  // Use TanStack Query to manage the state
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (demo) return demo()

      const snapshot = await getDoc(docRef)
      if (snapshot.exists()) {
        return {
          ...snapshot.data(),
          [idField]: snapshot.id,
        } as T & { id?: string }
      }
      return undefined
    },
    // Keep data in cache indefinitely (real-time updates handle freshness)
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !options.disabled,
  })

  return {
    data: query.data,
    status: query.status,
    error: query.error,
  }
}
